import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import Button from "@mui/material/Button";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Autocomplete, Checkbox, TextField, Tooltip } from "@mui/material";
import SearchForm, { useSearchFormContext } from "../../component/searchPage/SearchForm";
import SearchDropdown from "../../component/searchPage/SearchDropdown";
import useShiftRangeSelect from "../../component/searchPage/useShiftRangeSelect";
import SearchDateInput from "../../component/searchPage/SearchDateInput";
import { downloadVgsapBulletinPDF, getVgsapSearchCriteria, getVgsapSearchResults } from "../../service/SalesNewsVgsap";
import { PdfLink } from "../../component/searchPage/PdfLink";
import { useTranslation } from "react-i18next";
import { downloadAllVgsapResult } from "../../service/SalesNewsVgsap";
import { usePushToDataLayer } from "../../hooks/usePushToDataLayer";
import useUser from "../../hooks/useUser";
import useDebounce from "../../hooks/useDebounce";
import { GTM_EVENTS } from "../../utils/constants";
import { useQuery } from "@tanstack/react-query";
import { useWatch, useController } from "react-hook-form";
import "./salesNewsVgsap.css";

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ROUTES = {
	VGSAP_REPORT: "/sales-news/sales-news-vgsap/report",
	VGSAP_DETAIL: "/sales-news/sales-news-vgsap/detail",
	VGSAP_PROMO_DETAIL: "/sales-news/sales-news-vgsap/promotion",
	VGSAP_PROMO_EDIT: "/sales-news/sales-news-vgsap/promotion/edit",
};

const SEARCH_SCOPE_OPTIONS = [
	{ key: "SBDetail", value: "Sales News" },
	{ key: "SBDetailPromo", value: "Promotion Summaries" },
];

const REGION_OPTIONS = [
	{ key: "", value: "All" },
	{ key: "US00", value: "US" },
	{ key: "CA00", value: "Canada" },
];

// Fiscal year starts April 1. If before April, use previous year's April 1.
function getFiscalYearStart() {
	const now = new Date();
	const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
	return `${year}-04-01`;
}

function getToday() {
	return new Date().toISOString().split('T')[0];
}

// Returns date 10 years ago from today (auto-adjusts each year)
function getTenYearsAgo() {
	const now = new Date();
	return `${now.getFullYear() - 10}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// Returns date 2 years from today (for admin End Date max)
function getTwoYearsFromNow() {
	const now = new Date();
	return `${now.getFullYear() + 2}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const exportToolbar = (t) => ({
	type: 'bulletins',
	pdf: {
		download_selected: {
			isrequired: true,
			label: t('Download Selected Files (PDF)'),
			warning: t('Please select at least one Sales News'),
			severity: 'warning'
		},
		download_all: {
			isrequired: true,
			label: t('Download All Files (PDF)')
		}
	},
	excel: {
		download_selected: {
			isrequired: true,
			label: t('Export Selected to Excel'),
			warning: t('Please select at least one Sales News'),
			severity: 'warning'
		},
		download_all: {
			isrequired: true,
			label: t('Export All to Excel')
		}
	},
	email: {
		email_selected_pdf: {
			isrequired: true,
			label: t('Email Selected Files (PDF)'),
			warning: t('Please select at least one Sales News'),
			severity: 'warning'
		},
		email_all_pdf: {
			isrequired: true,
			label: t('Email All Files (PDF)')
		}
	}
});

function renderLink(params) {
	const { bulletin_no, link } = params.value;
	return (
		<Box className="vgsap__cell-link">
			<PdfLink href={link} tabIndex={params.tabIndex}>
				{bulletin_no}
			</PdfLink>
			<Link to={`${ROUTES.VGSAP_DETAIL}/${bulletin_no}`} className="vgsap__view-link">
				View Details
			</Link>
		</Box>
	);
}

import { formatDateLocale as formatDate } from "./vgsapUtils";

const bulletinColumns = (t, isSalesNewsAdmin) => [
	{ field: "numberAndLink", headerName: t("Sales News No"), width: 200, renderCell: renderLink, valueFormatter: (value) => `${value.bulletin_no}` },
	{ field: "bulletin_dt", headerName: t("Sales News Date"), width: 140, sort: "desc", valueFormatter: (value) => formatDate(value) },
	{ field: "promo_start", headerName: t("Promo Start"), width: 130, valueFormatter: (value) => formatDate(value) },
	{ field: "promo_end", headerName: t("Promo End"), width: 130, valueFormatter: (value) => formatDate(value) },
	{
		field: "bulletin_desc",
		headerName: t("Description"),
		minWidth: 250,
		flex: 1,
	},
	{ field: "bulletin_type", headerName: t("Type"), width: 150 },
	{ field: "designation", headerName: t("Designation"), width: 150 },
	{ field: "company", headerName: t("Business Unit"), width: 160 },
	{ field: "company_id", headerName: t("Region"), width: 110 },
];

// Promotion Summaries scope — columns matching RefLib
function renderPromoLink(params) {
	const { promo_id, bulletin_no } = params.row;
	return (
		<Box className="vgsap__cell-link">
			<Link to={`${ROUTES.VGSAP_PROMO_DETAIL}/${promo_id}`} className="vgsap__promo-link">
				{promo_id}
			</Link>
			<Link to={`${ROUTES.VGSAP_PROMO_EDIT}/${promo_id}`} className="vgsap__edit-link">
				<EditIcon fontSize="small" sx={{ mr: 0.3 }} />Edit
			</Link>
		</Box>
	);
}

const promoColumns = (t) => [
	{ field: "promo_id", headerName: t("Promo ID"), width: 200, renderCell: renderPromoLink, sort: "desc" },
	{ field: "description", headerName: t("Description"), minWidth: 280, flex: 1 },
	{ field: "start_date", headerName: t("Promo Start"), width: 130, valueFormatter: (value) => formatDate(value) },
	{ field: "end_date", headerName: t("Promo End"), width: 130, valueFormatter: (value) => formatDate(value) },
	{ field: "bulletin_no", headerName: t("Sales News"), width: 160 },
	{ field: "scd_code", headerName: t("CPG"), width: 130 },
];

// Dynamic column function — switches columns based on search scope in response
const getColumns = (t, isSalesNewsAdmin) => (searchResultData) => {
	if (searchResultData?.searchScope === "SBDetailPromo") {
		return promoColumns(t);
	}
	return bulletinColumns(t, isSalesNewsAdmin);
};

// Dynamic dropdown that switches between Sales News No / Promo No based on Search Scope
// Resets selection when scope changes
// Re-fetches dropdown options when dates change
function ScopeAwareDropdown() {
	const { t } = useTranslation();
	const { isSalesNewsAdmin } = useUser();
	const { control, criteriaData } = useSearchFormContext();
	const searchScope = useWatch({ control, name: 'searchScope' });
	const sdate = useWatch({ control, name: 'sdate' });
	const edate = useWatch({ control, name: 'edate' });

	const isPromo = searchScope?.key === 'SBDetailPromo';
	const dataLabel = isPromo ? 'promoIds' : 'bulletinNos';
	const label = isPromo ? t('Promo No') : t('Sales News No');

	// Only re-fetch when dates differ from the defaults (initial load handled by criteriaFn).
	// defaultEnd must match the form's default End Date (admin: 2 years out) so the initial
	// dropdown list covers the same range the user sees selected.
	const defaultStart = getFiscalYearStart();
	const defaultEnd = isSalesNewsAdmin ? getTwoYearsFromNow() : getToday();
	const datesChanged = (sdate && sdate !== defaultStart) || (edate && edate !== defaultEnd);

	const { data: filteredCriteria, isFetching: isDropdownFetching } = useQuery({
		queryKey: ["vgsapDropdownOptions", sdate, edate],
		queryFn: () => getVgsapSearchCriteria(sdate || defaultStart, edate || defaultEnd),
		enabled: !!datesChanged,
		staleTime: 60000,
	});

	// Type-ahead / paste searches across ALL records (date-agnostic) via the
	// existing criteria endpoint with a `term`. The date-filtered list is kept
	// as the default (empty input) options.
	const [inputValue, setInputValue] = useState("");
	const debouncedTerm = useDebounce(inputValue.trim(), 300);
	const hasTerm = debouncedTerm.length > 0;

	const { data: termCriteria, isFetching: isTermFetching } = useQuery({
		queryKey: ["vgsapNumberSearch", isPromo ? "promo" : "bulletin", debouncedTerm],
		queryFn: () => getVgsapSearchCriteria(undefined, undefined, debouncedTerm),
		enabled: hasTerm,
		staleTime: 60000,
	});

	// Default (empty input) options: date-filtered list when dates changed, else initial criteria.
	const defaultOptions = (datesChanged && filteredCriteria?.[dataLabel]) || criteriaData?.[dataLabel] || [];
	// When searching, use server (all-records) results; otherwise the default list.
	const options = hasTerm ? (termCriteria?.[dataLabel] || []) : defaultOptions;
	const isLoading = hasTerm ? isTermFetching : (datesChanged && isDropdownFetching);

	// Controller for the dropdown value
	const { field } = useController({ control, name: dataLabel, defaultValue: [] });

	// Shift+Arrow / Shift+click range selection over the currently displayed
	// options (default date-filtered list or term-search results).
	const isEqual = (a, b) => a?.value === b?.value;
	// Track the filtered options actually shown so Shift-range spans exactly
	// what the user sees (respects the type-to-search filter).
	const filteredOptionsRef = useRef(options);

	// Commits a raw selection array: normalise string entries to {key,value},
	// update the form field, then clear the typed term so the default
	// date-filtered list returns for the next interaction. Shared by the normal
	// onChange path and the Shift-range path so both behave identically.
	const commitSelection = (v) => {
		const mapped = v.map(item => (typeof item === 'string' ? { key: item, value: item } : item));
		field.onChange(mapped);
		setInputValue("");
	};
	const commitTypedValue = (text = inputValue) => {
		const typed = (text || "").trim();
		if (!typed) return false;
		const current = field.value || [];
		const alreadySelected = current.some((item) => item?.value === typed);
		if (alreadySelected) {
			setInputValue("");
			return true;
		}
		commitSelection([...current, typed]);
		return true;
	};
	const { getAutocompleteProps, handleOptionClick } = useShiftRangeSelect({
		getOptions: () => filteredOptionsRef.current,
		getValue: () => field.value || [],
		onRangeSelect: (next) => commitSelection(next),
		isEqual,
	});

	// Reset selection when scope changes (but not on initial mount).
	// Depend only on searchScope.key — `field` from useController is a fresh object
	// each render, so including it would clear the selection on every render.
	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		field.onChange([]);
		setInputValue("");
	}, [searchScope?.key]);

	return (
		<div className={`searchForm multi-select-dropdown`}>
			<label htmlFor={dataLabel}>{label}</label>
			<Autocomplete
				id={dataLabel}
				multiple
				disableCloseOnSelect
				{...getAutocompleteProps()}
				loading={isLoading}
				loadingText={t('Loading...')}
				noOptionsText={t('No options')}
				isOptionEqualToValue={(option, value) => option?.value === value?.value}
				options={options}
				filterOptions={(opts, state) => {
					// When searching, the server already filtered against ALL records —
					// return as-is. For the default list, filter client-side.
					const shown = hasTerm
						? opts.slice(0, 100)
						: opts.filter(o => o.value?.toLowerCase().includes(state.inputValue.toLowerCase())).slice(0, 100);
					// Remember what's visible for Shift-range math.
					filteredOptionsRef.current = shown;
					return shown;
				}}
				inputValue={inputValue}
				onInputChange={(e, v, reason) => {
					// Only track text the user actually types/pastes. In `multiple`
					// mode MUI fires onInputChange with reason 'reset' after each
					// selection/render; clearing on 'reset' would wipe the typed term
					// before the debounce runs, so the server search never fires.
					if (reason === 'input') {
						setInputValue(v);
					} else if (reason === 'clear') {
						setInputValue("");
					}
				}}
				sx={{ width: 300 }}
				onChange={(e, v) => {
					commitSelection(v);
				}}
				value={field.value || []}
				getOptionLabel={(option) => option?.value || ""}
				renderOption={(props, option, { selected }) => {
					const { onClick, ...liProps } = props;
					return (
						<li
							{...liProps}
							key={option.key || option.value}
							onClick={(event) => {
								// Shift+click extends the range from the anchor; otherwise
								// fall through to MUI's default single-toggle.
								if (handleOptionClick(event, option)) return;
								onClick?.(event);
							}}
						>
							<Checkbox
								icon={checkboxIcon}
								checkedIcon={checkedIcon}
								style={{ marginRight: 8 }}
								checked={selected}
							/>
							{option?.value || ""}
						</li>
					);
				}}
				ListboxProps={{ style: { maxHeight: 300 } }}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder={`${label} (type to search)`}
						onBlur={(e) => {
							commitTypedValue();
							field.onBlur(e);
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								commitTypedValue();
							}
						}}
						onPaste={(e) => {
							const pasted = e.clipboardData?.getData("text") || "";
							if (pasted.trim()) {
								e.preventDefault();
								commitTypedValue(pasted);
							}
						}}
						name={field.name}
						sx={{ backgroundColor: "white" }}
						size="small"
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? <CircularProgress color="inherit" size={18} /> : null}
									{params.InputProps.endAdornment}
								</>
							),
						}}
					/>
				)}
			/>
		</div>
	);
}

function DescriptionField() {
	const { t } = useTranslation();
	const{ control } = useSearchFormContext();
	const { field } = useController({ name: "description", control });

	return (
		<div className="searchForm multi-select-dropdown">
			<label htmlFor="description">{t("Search Criteria")}</label>
			<TextField
				id="description"
				size="small"
				placeholder={t("Description (type to search)")}
				value={field.value || ""}
				onChange={(e) => field.onChange(e.target.value)}
				sx={{width: 300}}
			/>	
		</div>
	)
}

function SalesNewsVgsap() {
	const { t } = useTranslation();
	const pushToDL = usePushToDataLayer();
	const { isSalesNewsAdmin } = useUser();

	useEffect(() => {
		pushToDL(GTM_EVENTS.PAGE_LOAD, {
			page: {
				name: 'sales_news_vgsap',
				template: 'sales_news_vgsap'
			},
		});
	}, []);

	const reportAction = (
		<Link to={ROUTES.VGSAP_REPORT} className="vgsap__report-btn">
			<Button variant="outlined" size="small" startIcon={<AssessmentIcon />}>
				VIEW REPORT
			</Button>
		</Link>
	);

	return (
		<div className="vgsap__page">
			<SearchForm
				title={t('Sales News (Vistex GSAP)')}
				action={reportAction}
				columns={getColumns(t, isSalesNewsAdmin)}
				criteriaFn={() => getVgsapSearchCriteria(getFiscalYearStart(), isSalesNewsAdmin ? getTwoYearsFromNow() : getToday()).then(data => ({
					...data,
					companyList: [{ key: "All", value: "All" }, ...(data.companyList || [])],
					typeList: [{ key: "All", value: "All" }, ...(data.typeList || [])],
					designationList: [{ key: "All", value: "All" }, ...(data.designationList || [])],
					searchScope: isSalesNewsAdmin ? SEARCH_SCOPE_OPTIONS : SEARCH_SCOPE_OPTIONS.filter(opt => opt.key !== 'SBDetailPromo'),
					region: REGION_OPTIONS,
				}))}
				searchFn={getVgsapSearchResults}
				getAllResult={downloadAllVgsapResult}
				exportToolbar={exportToolbar(t)}
				downloadFns={{ title: 'SalesNewsVGSAP', zip: downloadVgsapBulletinPDF }}
				defaultValues={{
					searchScope: SEARCH_SCOPE_OPTIONS[0],
					companyList: [{ key: "All", value: "All" }],
					typeList: [{ key: "All", value: "All" }],
					designationList: [{ key: "All", value: "All" }],
					region: REGION_OPTIONS[0],
					sdate: getFiscalYearStart(),
					edate: isSalesNewsAdmin ? getTwoYearsFromNow() : getToday(),
					bulletinNos: [],
					promoIds: [],
					description: "",
				}}
				customButtons={({ setSearchData, getValues }) => (
					<Tooltip title={t("Results include Sales News published within the past 14 days.")}>
						<Button
							variant="outlined"
							size="small"
							startIcon={<HistoryIcon />}
							onClick={() => {
								// Show Recent: past 14 days up to today (dynamic).
								// Filters results only — the visible Start/End Date fields
								// are intentionally left unchanged.
								const today = getToday();
								const fourteenDaysAgo = new Date();
								fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
								const sdate = fourteenDaysAgo.toISOString().split('T')[0];

								// Keep the visible form as-is (the selected Sales News No /
								// Promo No chip stays), but for THIS search only send the
								// 14-day date range and drop the number selection so the
								// results are strictly the past 14 days. (A selected number
								// would otherwise override the date range via the
								// number-wins rule and show that record instead.)
								const current = getValues() || {};
								const updated = { ...current, sdate, edate: today, bulletinNos: [], promoIds: [], description:"" };

								// Trigger the search with the merged criteria. Sorting is
								// left at its current model (default: Sales News Date desc).
								setSearchData(updated);
							}}
							sx={{ ml: 1 }}
						>
							{t("Show Recent")}
						</Button>
					</Tooltip>
				)}>
				<div className="search__body--column">
					<SearchDropdown label={t('Search Scope')} dataLabel='searchScope' useFirstAsDefault />
					<ScopeAwareDropdown />
					<SearchDropdown label={t('Company')} dataLabel='companyList' multiple commitTypedOnSearch />
					<SearchDropdown label={t('Type')} dataLabel='typeList' multiple commitTypedOnSearch />
					<DescriptionField/>
				</div>
				<div className="search__body--column">
					<SearchDropdown label={t('Region')} dataLabel='region' useFirstAsDefault />
					<SearchDropdown label={t('Designation')} dataLabel='designationList' multiple commitTypedOnSearch />
					<SearchDateInput label={t('Start Date')} dataLabel='sdate' defaultValue={getFiscalYearStart()} minDate={getTenYearsAgo()} maxDate={isSalesNewsAdmin ? undefined : getToday()} compareWith='edate' comparison='notAfter' />
					<SearchDateInput label={t('End Date')} dataLabel='edate' defaultValue={isSalesNewsAdmin ? getTwoYearsFromNow() : getToday()} minDate={getTenYearsAgo()} maxDate={isSalesNewsAdmin ? getTwoYearsFromNow() : getToday()} compareWith='sdate' comparison='notBefore' />
				</div>
			</SearchForm>
		</div>
	);
}

export default SalesNewsVgsap;
